import React, { memo, useEffect, useLayoutEffect, useRef } from 'react';
import { CheckIcon, PlusIcon, TextAlignLeftIcon } from '@radix-ui/react-icons';
import { cn } from '../../utils/cn';
import { colorClass } from './palette';

const MAX_TEXT = 200;

const MindMapNode = memo(function MindMapNode({
  node,
  isRoot,
  selected,
  editing,
  side,
  hiddenCount,
  onSize,
  onText,
  onCommit,
  onAddChild,
}) {
  const ref = useRef(null);
  const inputRef = useRef(null);

  useLayoutEffect(() => {
    const element = ref.current;
    const report = () => onSize(node.id, element.offsetWidth, element.offsetHeight);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(element);
    return () => observer.disconnect();
  }, [node.id, onSize]);

  useEffect(() => {
    if (!editing) return;
    if (document.activeElement === inputRef.current) return;
    inputRef.current?.focus({ preventScroll: true });
    inputRef.current?.select();
  }, [editing]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onCommit(node.id);
    } else if (event.key === 'Escape') {
      onCommit(node.id);
    } else if (event.key === 'Tab') {
      event.preventDefault();
      onCommit(node.id);
      onAddChild(node.id);
    }
  };

  return (
    <div
      ref={ref}
      data-node-id={node.id}
      className={cn(
        'mm-node',
        colorClass(node.color),
        isRoot && 'mm-root',
        selected && 'mm-selected',
        node.done && 'mm-done'
      )}
      style={{ transform: `translate(${node.x}px, ${node.y}px) translate(-50%, -50%)` }}
    >
      {node.done && <CheckIcon className="mm-check" aria-label="Done" />}
      {editing ? (
        <span className="mm-edit" data-value={`${node.text}\u200b`}>
          <textarea
            ref={inputRef}
            rows={1}
            maxLength={MAX_TEXT}
            value={node.text}
            placeholder="Idea…"
            aria-label="Idea text"
            onChange={(event) => onText(node.id, { text: event.target.value })}
            onKeyDown={handleKeyDown}
            onBlur={() => onCommit(node.id)}
          />
        </span>
      ) : (
        <span className="mm-text">{node.text || <span className="mm-placeholder">Untitled</span>}</span>
      )}
      {node.note && !editing && <TextAlignLeftIcon className="mm-note-icon" aria-label="Has notes" />}
      {hiddenCount > 0 && (
        <button
          type="button"
          data-action="toggle-collapse"
          className={cn('mm-badge', side < 0 ? 'mm-badge-left' : 'mm-badge-right')}
          aria-label={`Expand ${hiddenCount} hidden ideas`}
        >
          +{hiddenCount}
        </button>
      )}
      {hiddenCount === 0 && !editing && (
        <button
          type="button"
          data-action="add-child"
          tabIndex={-1}
          className={cn('mm-add', side < 0 ? 'mm-add-left' : 'mm-add-right')}
          aria-label="Add child idea"
          title="Add child idea"
        >
          <span className="mm-add-dot">
            <PlusIcon />
          </span>
        </button>
      )}
    </div>
  );
});

export default MindMapNode;
